import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, defineMessages } from 'react-intl';
import styled from 'styled-components';
import classNames from 'classnames';
import {
    Colours,
    Sizes,
    Typography,
    Paragraph,
    MessagePropType,
} from '@ratehub/base-ui';


function QuoteSummary({ title, isCompleted, monthlyRate, yearlyRate, ...otherProps }) {
    const intl = useIntl();

    return (
        <QuoteSummary.FlexContainer {...otherProps}>
            <div>
                <Choose>
                    <When condition={Array.isArray(title)}>
                        <For
                            each="item"
                            of={title}
                        >
                            <QuoteSummary.Title
                                forwardedAs="h4"
                                key={item}
                            >
                                {item}
                            </QuoteSummary.Title>
                        </For>
                    </When>
                    <When condition={title != null}>
                        <QuoteSummary.Title
                            forwardedAs="h4"
                            message={title}
                        />
                    </When>
                </Choose>

                <If condition={!isCompleted}>
                    <QuoteSummary.Provider
                        size="small"
                        message={Messages.MORE_INFO}
                    />
                </If>
            </div>

            <QuoteSummary.AmountContainer>
                <p className={classNames('rh-title-2xl', 'rh-m-0')}>
                    {intl.formatMessage(Messages.PER_MONTH, {
                        amount: monthlyRate
                            ? intl.formatNumber(monthlyRate, {
                                style: 'currency',
                                currency: 'CAD',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                            })
                            : intl.formatMessage(Messages.EMPTY_DOLLAR_AMOUNT),
                    })}
                </p>
                <If condition={yearlyRate}>
                    <QuoteSummary.YearlyAmount size="medium">
                        {intl.formatMessage(Messages.PER_YEAR, {
                            amount: intl.formatNumber(yearlyRate, {
                                style: 'currency',
                                currency: 'CAD',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                            }),
                        })}
                    </QuoteSummary.YearlyAmount>
                </If>
            </QuoteSummary.AmountContainer>
        </QuoteSummary.FlexContainer>
    );
}

QuoteSummary.propTypes = {
    title: PropTypes.oneOfType([
        MessagePropType,
        PropTypes.arrayOf(PropTypes.string),
    ]),
    isCompleted: PropTypes.bool,
    monthlyRate: PropTypes.number,
    yearlyRate: PropTypes.number,
};

QuoteSummary.defaultProps = {
    title: undefined,
    isCompleted: undefined,
    monthlyRate: null,
    yearlyRate: null,
};


const Messages = defineMessages({
    PER_MONTH: {
        id: 'Accounts.quoteSummaryRow.perMonth',
        defaultMessage: '{amount}/mo',
    },
    PER_YEAR: {
        id: 'Accounts.quoteSummaryRow.perYear',
        defaultMessage: '{amount}/year',
    },
    EMPTY_DOLLAR_AMOUNT: {
        id: 'Accounts.quoteSummaryRow.emptyDollarAmount',
        defaultMessage: '$-',
    },
    MORE_INFO: {
        id: 'Accounts.quoteSummaryRow.requiredMoreInfo',
        defaultMessage: 'Required more information to generate estimate',
    },
});



QuoteSummary.FlexContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: ${Sizes.SPACING.ONE};
`;

QuoteSummary.Title = styled(Paragraph)`
    margin: 0 ${Sizes.SPACING.ONE_AND_A_HALF} ${Sizes.SPACING.HALF} 0;
    font-weight: ${Typography.WEIGHTS.MEDIUM};
`;

QuoteSummary.Provider = styled(Paragraph)`
    margin: 0;
    font-style: italic;
    color: ${Colours.STONE_DARKEST};
`;

QuoteSummary.AmountContainer = styled.div`
    text-align: right;
`;

QuoteSummary.YearlyAmount = styled(Paragraph)`
    margin: 0;
    font-size: ${Sizes.FONTS.S};
    color: ${Colours.STONE_DARKEST};
`;

export default QuoteSummary;
